import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronRight,
  Compass,
  Flag,
  Flower2,
  Home as HomeIcon,
  Mail,
  Settings2,
  Shell,
  Sparkles,
  Sun,
  Volume2,
  VolumeX,
  Music2,
  X,
  RefreshCw,
  Download,
  LogOut,
  Cloud,
  CloudOff,
  Printer,
  Heart,
} from "lucide-react";
import { Animal, Island } from "./Art";
import { Lesson } from "./Lesson";
import { useIsland, type IslandStore } from "./store";
import { setMuted, speak, stopAudio } from "./audio";
import { setMusicMuted, setMusicVolume, startMusic, stopMusic } from "./music";
import { chineseSparkForJourney, englishSpark } from "../shared/language";
import {
  QUESTS,
  CHAPTERS,
  FRIENDS,
  FACTS,
  SCENES,
  completed,
  conceptEvidence,
  COSMETICS,
  CURRICULUM_AREAS,
  decorations,
 ownedCosmetics,
  profile,
  progress,
  unlocked,
  makeSession,
  makeTask,
  makePicnicSession,
  makeCurriculumSession,
  prepareTasks,
  rhyme,
  day,
  type Scene,
  type Session,
  type Answer,
} from "../shared/game";
type Page = "home" | "island" | "letters";
const FIXED_LOGIN_USERNAME = "13107640679";

function LoginScreen({ store }: { store: IslandStore }) {
  const [username, setUsername] = useState(FIXED_LOGIN_USERNAME);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await store.auth("login", username.trim(), password, false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="login-page">
      <div className="login-card">
        <div className="login-logo"><Shell size={30} /></div>
        <p className="eyebrow">LITTLE ISLAND, BIG DISCOVERIES</p>
        <h1>小岛成长记</h1>
        <p className="login-lede">登录后，继续建设属于你的数学小岛。</p>
        <form onSubmit={submit}>
          <label>
            账号
            <input
              autoComplete="username"
              inputMode="numeric"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </label>
          <label>
            密码
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary full login-submit" disabled={busy || !store.online}>
            {busy ? "正在进入小岛…" : store.online ? "进入小岛" : "等待网络连接…"}
          </button>
        </form>
        <p className="login-note">手机打开网址即可玩，学习记录会保存在这座家庭小岛里。</p>
      </div>
    </main>
  );
}

export default function App() {
  const store = useIsland();
  const [page, setPage] = useState<Page>("home");
  const [parent, setParent] = useState(false);
  const [library, setLibrary] = useState(false);
  const [choosing, setChoosing] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [reward, setReward] = useState<Session | null>(null);
  const legacyMute = localStorage.getItem("island-muted") === "true";
  const [voiceMuted, setVoiceMuted] = useState(() => localStorage.getItem("island-voice-muted") === null ? legacyMute : localStorage.getItem("island-voice-muted") === "true");
  const [musicMuted, setMusicMutedState] = useState(() => localStorage.getItem("island-music-muted") === null ? legacyMute : localStorage.getItem("island-music-muted") === "true");
  const [musicVolume, setMusicVolumeState] = useState(() => {
    const savedVolume = Number(localStorage.getItem("island-music-volume") || 18);
    return Number.isFinite(savedVolume) ? Math.max(0, Math.min(25, savedVolume)) : 18;
  });
  const [englishSkipped, setEnglishSkipped] = useState(() => localStorage.getItem("island-english-skipped") === "true");
  const [toast, setToast] = useState("");
  const done = completed(store.events),
    person = profile(store.events),
    next = QUESTS.find((q) => !done.has(q.id)) || QUESTS[0];
  const states = progress(store.events);
  const seen = [...states.values()].filter((p) => p.seen);
  const due = seen.filter((p) => p.due <= Date.now());
  const learned = seen.filter((p) => ["initial", "steady"].includes(p.status));
  const days = [
    ...new Set(
      store.events.filter((e) => e.type === "answer").map((e) => day(e.at)),
    ),
  ];
  const journeySpark = reward && ["story", "course"].includes(reward.mode)
    ? chineseSparkForJourney(reward.tasks.map((task) => task.conceptId || ""), reward.mode, `${reward.quest}-${reward.id}`)
    : null;
  const journeyEnglish = reward && ["story", "course"].includes(reward.mode) && reward.tasks[0]
    ? englishSpark(reward.tasks[0])
    : null;
  useEffect(() => {
    setMuted(voiceMuted);
    localStorage.setItem("island-voice-muted", String(voiceMuted));
    localStorage.setItem("island-muted", String(voiceMuted && musicMuted));
  }, [voiceMuted, musicMuted]);
  useEffect(() => {
    setMusicMuted(musicMuted);
    setMusicVolume(musicVolume / 100);
    localStorage.setItem("island-music-muted", String(musicMuted));
    localStorage.setItem("island-music-volume", String(musicVolume));
  }, [musicMuted, musicVolume]);
  const changeMusicMuted = (muted: boolean) => {
    setMusicMutedState(muted);
    setMusicMuted(muted);
    if (!muted) void startMusic();
  };
  useEffect(() => {
    localStorage.setItem("island-english-skipped", String(englishSkipped));
  }, [englishSkipped]);
  useEffect(() => {
    if (toast) {
      const id = setTimeout(() => setToast(""), 3500);
      return () => clearTimeout(id);
    }
  }, [toast]);
  if (!store.account) return <LoginScreen store={store} />;
  const start = (
    scene: Scene,
    quest?: string,
    mode: Session["mode"] = "story",
  ) => {
    const actualMode =
      mode === "story" && !store.events.some((e) => e.type === "baseline")
        ? "baseline"
        : mode;
    store.setSession(makeSession(store.events, scene, actualMode, quest));
    setChoosing(null);
    setPlaying(true);
    setParent(false);
    stopAudio();
    void startMusic();
    window.scrollTo(0, 0);
  };
  const begin = () => {
    if (store.session) {
      store.setSession({ ...store.session, started: Date.now() });
      setPlaying(true);
      void startMusic();
      return;
    }
    setChoosing(next.id);
  };
  const finish = (s: Session) => {
    if (s.mode === "story")
      store.add("complete", { quest: s.quest, session: s.id });
    if (s.mode === "baseline") store.add("baseline", { finished: true });
    if (s.mode === "assessment")
      store.add("assessment", { checkpoint: s.checkpoint ?? 0, session: s.id });
    if (["story", "review", "baseline", "course"].includes(s.mode))
      store.awardDailyShells(day(Date.now()));
    store.setSession(null);
    setPlaying(false);
    setReward(s);
    window.scrollTo(0, 0);
  };
  if (playing && store.session)
    return (
      <Lesson
        store={store}
        voiceMuted={voiceMuted}
        musicMuted={musicMuted}
        onVoiceMutedChange={setVoiceMuted}
        onMusicMutedChange={changeMusicMuted}
        onExit={() => {
          setPlaying(false);
          stopAudio();
          stopMusic();
          if (Date.now() - store.session!.started >= 10 * 60000)
            setToast("已经冒险一会儿了，先休息一下吧。下次从这里继续！");
        }}
        onFinish={finish}
      />
    );
  const weekly = days.filter(
    (d) => Date.parse(d + "T23:59:59+08:00") >= Date.now() - 7 * 86400000,
  ).length;
  return (
    <div className="app-shell">
      <header className="site-header">
        <a
          href="#"
          className="brand"
          onClick={(e) => {
            e.preventDefault();
            setPage("home");
          }}
          aria-label="小岛成长记首页"
        >
          <span className="brand-mark">
            <Shell size={27} />
          </span>
          <span>
            小岛成长记<small>LITTLE ISLAND, BIG DISCOVERIES</small>
          </span>
        </a>
        <nav className="desktop-nav" aria-label="主要导航">
          {[
            { id: "home", label: "今天的冒险", icon: Compass },
            { id: "island", label: "我的小岛", icon: HomeIcon },
            { id: "letters", label: "伙伴来信", icon: Mail },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={page === id ? "active" : ""}
              onClick={() => setPage(id as Page)}
            >
              <Icon size={18} />
              {label}
              {id === "letters" && due.length > 0 && (
                <i className="notification-dot" />
              )}
            </button>
          ))}
        </nav>
        <div className="header-actions">
          <details className="audio-preferences">
            <summary className="icon-button" aria-label="声音和语言设置"><Music2 size={19} /><Volume2 size={17} /></summary>
            <div className="audio-preferences-panel">
              <label><span><Volume2 size={17} />伙伴语音</span><input type="checkbox" checked={!voiceMuted} onChange={(e) => setVoiceMuted(!e.target.checked)} /></label>
              <label><span><Music2 size={17} />背景音乐</span><input type="checkbox" checked={!musicMuted} onChange={(e) => changeMusicMuted(!e.target.checked)} /></label>
              <label className="audio-volume"><span>音乐音量</span><input type="range" min="0" max="25" value={musicVolume} onChange={(e) => setMusicVolumeState(Number(e.target.value))} /></label>
              <label><span>跳过英语彩蛋</span><input type="checkbox" checked={englishSkipped} onChange={(e) => setEnglishSkipped(e.target.checked)} /></label>
            </div>
          </details>
          <button className="parent-link" onClick={() => setParent(true)}>
            <Settings2 size={17} />
            <span>家长小站</span>
          </button>
        </div>
      </header>
      <main className="main-wrap">
        {page === "home" && (
          <>
            <section className="hero">
              <div className="hero-copy">
                <span className="eyebrow">
                  <span className="tiny-sun">☀</span> 海风正好，一起出发吧
                </span>
                <h1>
                  小小的发现，
                  <br />
                  让小岛<span>长大一点。</span>
                </h1>
                <p>
                  和{person.pet}一起种花、开店、交朋友。
                  <br />
                  每一个新本领，都让这里更美好。
                </p>
                <button className="primary hero-button" onClick={begin}>
                  {store.session
                    ? "继续上次的冒险"
                    : !store.events.some((e) => e.type === "baseline")
                      ? "开启我的小岛冒险"
                      : "开始今天的冒险"}
                  <ArrowRight size={21} />
                </button>
                <span className="hero-footnote">
                  <Sun size={15} />
                  每天约10分钟 · 慢慢想，也很棒
                </span>
                <div className="pet-note">
                  <Animal size={73} />
                  <p>
                    “
                    {done.size
                      ? `你已经完成${done.size}个委托啦。`
                      : "我叫泡泡，是你的新伙伴。"}
                    <br />
                    <strong>
                      {done.size
                        ? "今天又会有什么小发现呢？"
                        : "我们一起建一座小岛吧！"}
                    </strong>
                    ”
                  </p>
                </div>
              </div>
              <div className="hero-map">
                <Island
                  preview={done.size === 0}
                  items={
                    decorations(store.events).length
                      ? decorations(store.events)
                      : unlocked(store.events).map((building, i) => ({
                          building,
                          slot: i,
                          color: "coral",
                        }))
                  }
                />
                <div className="map-sticker sticker-top">
                  <span>✦</span> 每一个本领，都会开花
                </div>
                <div className="map-sticker sticker-bottom">
                  <span className="sticker-leaf">❧</span>
                  <span>
                    我们的成长小岛
                    <small>
                      {done.size
                        ? `${unlocked(store.events).length}处新风景，等你布置`
                        : "未来的小岛，由你一点点建成"}
                    </small>
                  </span>
                </div>
                <div className="map-compass">
                  N<span>✧</span>
                </div>
              </div>
            </section>
            <section className="adventure-section">
              <div className="section-heading">
                <div>
                  <span className="eyebrow">一步一步，把本领装进背包</span>
                  <h2>今天，去哪里探险？</h2>
                </div>
                <button
                  className="text-button"
                  onClick={() => setLibrary(true)}
                >
                  <BookOpen size={18} />
                  我的口诀小册 <ChevronRight size={16} />
                </button>
              </div>
              <div className="adventure-layout">
                <div className="quest-board">
                  <div className="chapter-heading">
                    <div className="chapter-badge">
                      <Flower2 size={24} />
                    </div>
                    <div>
                      <span>第 {next.chapter + 1} 章 · 乘法花园</span>
                      <h3>{CHAPTERS[next.chapter][0]}</h3>
                    </div>
                    <span className="chapter-count">
                      {done.size} / 27 个委托
                    </span>
                  </div>
                  <div className="quest-cards">
                    {QUESTS.filter((q) => q.chapter === next.chapter).map(
                      (q, i) => (
                        <button
                          key={q.id}
                          className={`quest-card ${q.id === next.id ? "current" : ""} ${done.has(q.id) ? "complete" : ""}`}
                          onClick={() => setChoosing(q.id)}
                        >
                          <span className="quest-num">
                            {done.has(q.id) ? (
                              <Check size={20} />
                            ) : (
                              String(i + 1).padStart(2, "0")
                            )}
                          </span>
                          <span className={`quest-picture scene-${i}`}>
                            <Animal
                              type={["rabbit", "panda", "bear"][i]}
                              size={88}
                            />
                          </span>
                          <strong>{q.title}</strong>
                          <span className="quest-kind">
                            {
                              [
                                "摆一摆 · 看懂几个几",
                                "想一想 · 记住小口诀",
                                "用一用 · 帮朋友解决问题",
                              ][i]
                            }
                          </span>
                          <span className="quest-action">
                            {done.has(q.id)
                              ? "再去玩一玩"
                              : q.id === next.id
                                ? "从这里出发"
                                : "我想试试看"}
                            <ArrowRight size={16} />
                          </span>
                        </button>
                      ),
                    )}
                  </div>
                  <article className="picnic-quest-card">
                    <div>
                      <span className="eyebrow">新故事 · 除法初体验</span>
                      <h3>小兔的野餐盒</h3>
                      <p>12块点心、3个盒子，怎样分才公平？</p>
                    </div>
                    <button
                      className="secondary"
                      onClick={() => {
                        store.setSession(makePicnicSession());
                        setPlaying(true);
                        setPage("home");
                        stopAudio();
                        void startMusic();
                      }}
                    >
                      帮小兔分一分 <ArrowRight size={17} />
                    </button>
                  </article>
                  <section className="curriculum-map">
                    <div className="section-heading compact">
                      <div>
                        <span className="eyebrow">17项知识目标 · 24种题型</span>
                        <h3>本学期数学地图</h3>
                      </div>
                      <span className="map-tip">挑一个想探索的地方</span>
                    </div>
                    <div className="curriculum-area-grid">
                      {CURRICULUM_AREAS.map((area) => (
                        <button
                          key={area.id}
                          className="curriculum-area-card"
                          onClick={() => {
                            store.setSession(makeCurriculumSession(area.id, store.events));
                            setPlaying(true);
                            stopAudio();
                            void startMusic();
                          }}
                        >
                          <strong>{area.title}</strong>
                          <span>{area.subtitle}</span>
                          <small>{area.templates.length}种玩法</small>
                        </button>
                      ))}
                    </div>
                  </section>
                  <p className="board-foot">
                    <Heart size={15} />
                    答错也没关系，泡泡会陪你一起想。
                  </p>
                </div>
                <aside className="footprint-card">
                  <div className="footprint-title">
                    <span className="icon-sun">
                      <Sun size={22} />
                    </span>
                    <h3>每一步，都算数</h3>
                  </div>
                  <p>
                    这周，我们相遇了 <b>{weekly}</b> 天
                  </p>
                  <div className="week-dots">
                    {Array.from({ length: 7 }, (_, i) => {
                      const date = new Date(Date.now() - (6 - i) * 86400000);
                      const active = days.includes(day(date.getTime()));
                      return (
                        <div key={i} className={active ? "active" : ""}>
                          <span>{active ? <Check size={13} /> : <span />}</span>
                          <small>
                            {
                              ["日", "一", "二", "三", "四", "五", "六"][
                                new Date(
                                  date.toLocaleString("en-US", {
                                    timeZone: "Asia/Shanghai",
                                  }),
                                ).getDay()
                              ]
                            }
                          </small>
                        </div>
                      );
                    })}
                  </div>
                  <div className="footprint-bottom">
                    <span className="shell-icon">🐚</span>
                    <p>
                      累计 <b>{days.length}</b> 个冒险日
                      <small>不用赶路，按自己的节奏就好。</small>
                    </p>
                  </div>
                  <button
                    className="text-button"
                    onClick={() => setPage("island")}
                  >
                    看看我的小岛 <ArrowRight size={15} />
                  </button>
                </aside>
              </div>
            </section>
            <section className="friends-strip">
              <div>
                <span className="eyebrow">小岛居民们</span>
                <h3>有朋友，冒险更有趣</h3>
              </div>
              <div className="friend-list">
                {FRIENDS.map((f) => (
                  <button
                    key={f.name}
                    onClick={() => {
                      setToast(
                        `${f.name}：${done.size ? "谢谢你让小岛变得更好！" : "很高兴认识你，一起建设小岛吧！"}`,
                      );
                      void speak(
                        `我是${f.name}，很高兴认识你！`,
                        `friend-${f.name}`,
                      );
                    }}
                  >
                    <Animal type={f.animal} size={64} />
                    <span>{f.name}</span>
                  </button>
                ))}
              </div>
              <span className="friends-note">每个人，都有一个小故事。</span>
            </section>
          </>
        )}
        {page === "island" && (
          <IslandPage store={store} onAdventure={begin} toast={setToast} />
        )}
        {page === "letters" && (
          <section className="letters-page">
            <div className="section-heading">
              <div>
                <span className="eyebrow">把学过的小本领，再用一次</span>
                <h1>伙伴来信</h1>
              </div>
              <span className="stamp">
                <Mail /> 小岛邮局
              </span>
            </div>
            <div className="letter-layout">
              <article className="letter-paper">
                <Animal type="bird" size={100} />
                <span className="eyebrow">寄给：{person.child}</span>
                <h2>
                  {due.length ? "朋友们想请你帮个忙" : "一封来自泡泡的信"}
                </h2>
                <p>亲爱的{person.child}：</p>
                <p>
                  {seen.length
                    ? `我们一起发现了${seen.length}句口诀的秘密。有些小本领，隔几天再用一用，就会记得更牢。你愿意来帮我们完成今天的小委托吗？`
                    : "小岛上有好多有趣的事情。先和我去摆一摆、数一数，发现新的本领。以后朋友们会寄来小委托，让你用上学过的数学。"}
                </p>
                <p className="letter-sign">你的朋友 · {person.pet} ☘</p>
                <button
                  className="primary"
                  onClick={() =>
                    seen.length
                      ? start("breakfast", undefined, "review")
                      : begin()
                  }
                >
                  {seen.length
                    ? due.length
                      ? `打开${Math.min(6, due.length)}个复习小委托`
                      : "练练学过的小本领"
                    : "去认识我的小岛"}
                  <ArrowRight size={19} />
                </button>
                <small>每次只做一点点，记忆会慢慢长大。</small>
              </article>
              <aside className="letter-aside">
                <div className="soft-card">
                  <Shell />
                  <h3>会摆 → 会算 → 会用</h3>
                  <p>
                    记住口诀是一种本领，知道它能帮谁解决问题，是另一种本领。
                  </p>
                  <button
                    className="text-button"
                    onClick={() => setLibrary(true)}
                  >
                    翻开口诀小册 <ArrowRight size={17} />
                  </button>
                </div>
                {learned.length > 0 && (
                  <div className="soft-card">
                    <Flag />
                    <h3>和昨天的自己比一比</h3>
                    <p>只练你熟悉的口诀，没有倒计时。完成后看看自己的用时。</p>
                    <button
                      className="secondary"
                      onClick={() => start("garden", undefined, "challenge")}
                    >
                      开始个人小挑战
                    </button>
                  </div>
                )}
                <div className="english-card">
                  <span>小小英语角 · 可跳过</span>
                  <h3>
                    Apple <span>苹果 🍎</span>
                  </h3>
                  <button
                    className="text-button"
                    onClick={() => void speak("Apple，苹果。", "english-apple")}
                  >
                    <Volume2 size={17} />
                    听一听
                  </button>
                </div>
              </aside>
            </div>
          </section>
        )}
        <footer className="site-footer">
          <span>☘ 用好奇心，慢慢长大。</span>
          <span>
            {store.account
              ? store.online
                ? store.pending
                  ? "小岛记录正在保存"
                  : "小岛记录已保存"
                : "离线冒险中，联网后自动保存"
              : "已在这台设备保存 · 家长小站可开启同步"}
          </span>
        </footer>
      </main>
      <nav className="mobile-nav" aria-label="手机导航">
        {[
          { id: "home", name: "今天的冒险", icon: Compass },
          { id: "island", name: "我的小岛", icon: HomeIcon },
          { id: "letters", name: "伙伴来信", icon: Mail },
        ].map(({ id, name, icon: Icon }) => (
          <button
            key={id}
            className={page === id ? "active" : ""}
            onClick={() => {
              setPage(id as Page);
              window.scrollTo(0, 0);
            }}
          >
            <Icon size={23} />
            <span>{name}</span>
          </button>
        ))}
      </nav>
      {choosing && (
        <Modal title="今天先帮谁呢？" onClose={() => setChoosing(null)}>
          <p className="modal-intro">
            选一位朋友，一起完成一个小委托。
            <br />
            学到的本领，都能帮小岛长大。
          </p>
          <div className="scene-choices">
            {(["breakfast", "garden", "harbor"] as Scene[]).map((scene, i) => (
              <button key={scene} onClick={() => start(scene, choosing)}>
                <Animal type={["rabbit", "panda", "bear"][i]} size={90} />
                <strong>{SCENES[scene].name}</strong>
                <span>
                  帮{SCENES[scene].friend}
                  {SCENES[scene].verb}
                </span>
                <ArrowRight size={19} />
              </button>
            ))}
          </div>
          {store.session && (
            <p className="note">
              开始新委托会替换未完成的任务，已经学过的记录会保留。
            </p>
          )}
        </Modal>
      )}
      {reward && (
        <Modal
          title={
            reward.mode === "baseline"
              ? "认识你，真开心！"
              : reward.mode === "assessment"
                ? "本领观察完成啦！"
                : reward.mode === "course"
                  ? "这一段数学探险完成啦！"
                  : "小岛，因为你长大了一点"
          }
          onClose={() => setReward(null)}
        >
          <div className="reward">
            <div className="reward-stars">✦ ✧ ✦</div>
            <Animal size={138} />
            <p>
              {reward.mode === "story"
                ? `你完成了朋友的委托！${unlocked(store.events).length}处小岛风景已经可以布置。`
                : reward.mode === "baseline"
                  ? "泡泡记下了你的起点，接下来会陪你找到适合自己的小任务。"
                  : reward.mode === "assessment"
                    ? "我们记下了这次独立尝试。每一次观察，都是为了找到更适合你的下一步。"
                    : reward.mode === "course"
                      ? "你认真想过、试过，也把一项数学本领用到了新任务里。今天的进度已经记下啦。"
                      : "旧本领又在新地方派上用场了。谢谢你的帮助！"}
            </p>
            {reward.mode === "challenge" && (
              <p>
                这次完成用了 {Math.round((Date.now() - reward.started) / 1000)}{" "}
                秒。下次也按自己的节奏来。
              </p>
            )}
            <div className="reward-summary">
              <span>
                <Check size={18} />
                {reward.tasks.length}个小步骤
              </span>
              <span>
                <Heart size={18} />
                又一次认真尝试
              </span>
            </div>
            <p className="note">
              {Date.now() - reward.started >= 10 * 60000
                ? "已经冒险一会儿了，站起来看看远处，和泡泡挥挥手吧。"
                : "去看看小岛的新变化，然后休息一下吧。"}
            </p>
            <button
              className="primary full"
              onClick={() => {
                const mode = reward.mode;
                setReward(null);
                if (mode === "story") setPage("island");
                else setPage("home");
              }}
            >
              {reward.mode === "story" || reward.mode === "course" ? "布置我的小岛" : "回到我的小岛"}
              <ArrowRight size={19} />
            </button>
            {journeySpark && <details className="journey-language-card">
              <summary>泡泡留了一句诗，想听就点开</summary>
              <blockquote>“{journeySpark.line}”</blockquote>
              <small>{journeySpark.source}</small>
              <p>{journeySpark.meaning}</p>
              <button className="text-button" onClick={() => void speak(journeySpark.spoken, `language-${journeySpark.id}`)}><Volume2 size={16} />听泡泡读一遍</button>
            </details>}
            {!englishSkipped && journeyEnglish && <details className="journey-english-card">
              <summary>想听一个英语小彩蛋吗？（可跳过）</summary>
              <strong>{journeyEnglish.phrase}</strong>
              <small>{journeyEnglish.meaning}</small>
              <button className="text-button" onClick={() => void speak(journeyEnglish.spoken, `english-${journeyEnglish.id}`)}><Volume2 size={16} />听一遍英语</button>
            </details>}
          </div>
        </Modal>
      )}
      {library && (
        <Modal
          title="我的口诀小册"
          onClose={() => {
            setLibrary(false);
            stopAudio();
          }}
          wide
        >
          <p className="modal-intro">
            45句小口诀，藏着81个乘法的秘密。点小喇叭听一听，也可以动手试一试。
          </p>
          <div className="rhyme-library">
            {FACTS.map((f) => (
              <div
                key={f.key}
                className={`rhyme-item ${states.get(f.key)!.status}`}
              >
                <button
                  onClick={() => void speak(rhyme(f.a, f.b), `rhyme-${f.key}`)}
                  aria-label={`朗读${rhyme(f.a, f.b)}`}
                >
                  <span>{rhyme(f.a, f.b)}</span>
                  <Volume2 size={16} />
                </button>
                <div>
                  <small>
                    {f.a} × {f.b} = {f.a * f.b}
                  </small>
                  <button
                    className="text-button"
                    onClick={() => {
                      const s = makeSession(store.events, "garden", "review");
                      s.tasks = prepareTasks([
                        makeTask("array", f.a, f.b, "garden", "practice-array"),
                        makeTask(
                          "recall",
                          f.b,
                          f.a,
                          "garden",
                          "practice-recall",
                        ),
                        makeTask(
                          "application",
                          f.a,
                          f.b,
                          "harbor",
                          "practice-use",
                        ),
                      ]);
                      store.setSession(s);
                      setLibrary(false);
                      setPlaying(true);
                    }}
                  >
                    试试看
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
      {parent && (
        <Parent
          store={store}
          onClose={() => setParent(false)}
          onAssessment={(checkpoint) => {
            store.setSession(
              makeSession(
                store.events,
                "harbor",
                "assessment",
                undefined,
                checkpoint,
              ),
            );
            setParent(false);
            setPlaying(true);
          }}
        />
      )}
      {toast && (
        <div className="toast" role="status">
          <Sparkles size={18} />
          {toast}
        </div>
      )}
    </div>
  );
}
function Modal({
  title,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  const dialog = useRef<HTMLElement>(null);
  useEffect(() => {
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const elements = [
          ...(dialog.current?.querySelectorAll<HTMLElement>(
            'button:not(:disabled),input:not(:disabled),a[href],[tabindex="0"]',
          ) || []),
        ];
        const first = elements[0],
          last = elements.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = old;
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        ref={dialog}
        className={`modal ${wide ? "wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <button
          autoFocus
          className="icon-button modal-close"
          aria-label="关闭"
          onClick={onClose}
        >
          <X />
        </button>
        <span className="eyebrow">LITTLE ISLAND · 小岛成长记</span>
        <h2>{title}</h2>
        {children}
      </section>
    </div>
  );
}
function IslandPage({
  store,
  onAdventure,
  toast,
}: {
  store: IslandStore;
  onAdventure: () => void;
  toast: (s: string) => void;
}) {
  const available = unlocked(store.events);
  const [building, setBuilding] = useState(available[0] || "");
  const [slot, setSlot] = useState(0);
  const [color, setColor] = useState("coral");
  const items = decorations(store.events);
  const person = profile(store.events);
  const done = completed(store.events);
  const owned = ownedCosmetics(store.events);
  const [shopMessage, setShopMessage] = useState("");
  const save = () => {
    const occupied = items.find(
      (i) => i.slot === slot && i.building !== building,
    );
    if (occupied) {
      toast("这块地已经有建筑了，选一块空地试试。");
      return;
    }
    store.add("decorate", { building, slot, color });
    toast(`${building}搬到新地方啦！`);
    requestAnimationFrame(() =>
      document
        .querySelector(".builder-map")
        ?.scrollIntoView({
          block: "start",
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
        }),
    );
  };
  return (
    <section className="island-page">
      <div className="section-heading">
        <div>
          <span className="eyebrow">每一处风景，都是努力的小纪念</span>
          <h1>{person.child}的小岛</h1>
        </div>
        <span className="island-count">
          <Flag size={18} />
          {done.size}个委托 · {available.length}处风景
        </span>
      </div>
      <div className="island-builder">
        <div className="builder-map">
          <Island
            items={items}
            onSlot={available.length ? setSlot : undefined}
            selected={slot}
          />
          {owned.size > 0 && (
            <div className="cosmetic-overlay" aria-label="已收藏的小岛装饰">
              {owned.has("picnic-cloth") && <span className="cloth" title="海风野餐布">🧺</span>}
              {owned.has("pet-hat") && <span className="hat" title="泡泡的小草帽">🧢</span>}
              {owned.has("garden-lights") && <span className="lights" title="花园星星灯">✨　✨</span>}
            </div>
          )}
          {available.length > 0 && (
            <div className="plot-picker" aria-label="选择建筑地块">
              {Array.from({ length: 9 }, (_, i) => (
                <button
                  key={i}
                  className={slot === i ? "selected" : ""}
                  aria-pressed={slot === i}
                  onClick={() => setSlot(i)}
                >
                  第{i + 1}块地
                </button>
              ))}
            </div>
          )}
          {!available.length && (
            <div className="empty-island-note">
              <Animal size={75} />
              <div>
                <strong>第一间小屋，等你来建</strong>
                <p>完成一个故事委托，就能获得早餐店。</p>
                <button className="primary" onClick={onAdventure}>
                  一起出发 <ArrowRight size={17} />
                </button>
              </div>
            </div>
          )}
        </div>
        <aside className="builder-tools">
          <span className="eyebrow">我的建设背包</span>
          <h2>
            把喜欢的风景
            <br />
            放在喜欢的地方
          </h2>
          <p>选建筑 → 点地图上的地块 → 放置</p>
          <div className="building-list">
            {available.map((b, i) => (
              <button
                key={b}
                className={building === b ? "selected" : ""}
                onClick={() => setBuilding(b)}
              >
                <span>
                  {["🏡", "🌷", "⛵", "💌", "🌳", "🗼", "🪑", "🌻", "🌬️"][i]}
                </span>
                {b}
              </button>
            ))}
            {!available.length && (
              <p className="note">背包还是空的，第一段故事就会带来新风景。</p>
            )}
          </div>
          {available.length > 0 && (
            <>
              <h3>挑一种喜欢的颜色</h3>
              <div className="color-choices">
                {[
                  { id: "coral", name: "珊瑚橙" },
                  { id: "sage", name: "海草绿" },
                  { id: "sun", name: "阳光黄" },
                ].map((c) => (
                  <button
                    aria-label={c.name}
                    aria-pressed={c.id === color}
                    className={`${c.id} ${c.id === color ? "selected" : ""}`}
                    key={c.id}
                    onClick={() => setColor(c.id)}
                  >
                    {c.id === color && <Check size={20} />}
                  </button>
                ))}
              </div>
              <button className="primary full" onClick={save}>
                放在第{slot + 1}块地 <Check size={18} />
              </button>
            </>
          )}
          <div className="pet-builder">
            <Animal size={77} />
            <p>
              {person.pet}
              <br />
              <small>“这里有家的样子啦。”</small>
            </p>
          </div>
        </aside>
      </div>
      <div className="weekly-celebration">
        <Sparkles />
        <div>
          <strong>我们一起完成的事</strong>
          <p>
            {done.size
              ? `累计完成${done.size}个委托，收集${available.length}处风景。你认真想过的每一步，都留在这里。`
              : "先从一个小委托开始，小岛会慢慢变成你喜欢的样子。"}
          </p>
        </div>
      </div>
      <section className="island-shop">
        <div className="section-heading">
          <div>
            <span className="eyebrow">完成冒险，攒下贝壳</span>
            <h2>小岛装饰小铺</h2>
          </div>
          <strong className="shell-balance">🐚 {store.shellBalance}</strong>
        </div>
        <p className="note">今天第一次完成完整冒险可获得10枚贝壳。答错或请伙伴帮忙都不会少拿。</p>
        <div className="cosmetic-grid">
          {COSMETICS.map((item) => (
            <article className="cosmetic-card" key={item.id}>
              <span className="cosmetic-art">{item.id === "picnic-cloth" ? "🧺" : item.id === "pet-hat" ? "🧢" : "✨"}</span>
              <strong>{item.name}</strong>
              <small>{item.price} 枚贝壳</small>
              <button
                className={owned.has(item.id) ? "secondary" : "primary"}
                disabled={owned.has(item.id)}
                onClick={() => setShopMessage(store.purchaseCosmetic(item.id))}
              >
                {owned.has(item.id) ? "已收藏" : "带回小岛"}
              </button>
            </article>
          ))}
        </div>
        {shopMessage && <p className="shop-message" role="status">{shopMessage}</p>}
      </section>
    </section>
  );
}
function Parent({
  store,
  onClose,
  onAssessment,
}: {
  store: IslandStore;
  onClose: () => void;
  onAssessment: (n: number) => void;
}) {
  const [tab, setTab] = useState<"learning" | "account" | "settings">(
    "learning",
  );
  const [action, setAction] = useState<"register" | "login">("register");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [importGuest, setImportGuest] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const person = profile(store.events);
  const [child, setChild] = useState(person.child),
    [pet, setPet] = useState(person.pet);
  const [notice, setNotice] = useState("");
  const p = progress(store.events);
  const goals = conceptEvidence(store.events);
  const conceptNames: Record<string, string> = {
    "SORT-01": "按标准分类", "SORT-02": "整理成表格", "MUL-01": "理解几个几",
    "MUL-02": "看懂阵列", "MUL-03": "1～6口诀", "MUL-04": "乘加乘减",
    "DIV-01": "平均分求每份", "DIV-02": "按份数分组", "DIV-03": "乘除事实组",
    "DIR-01": "方向与观察点", "DIR-02": "描述地图路线", "MEAS-01": "身体尺子",
    "MEAS-02": "厘米、米与估测", "MEAS-03": "读尺", "MEAS-04": "画线段",
    "MUL-05": "7～9口诀", "APP-01": "选择算法并检查",
  };
  const facts = [...p.values()];
  const learned = facts.filter(
    (f) => f.status === "initial" || f.status === "steady",
  );
  const helped = facts
    .filter((f) => f.helped || f.wrong)
    .sort((a, b) => b.wrong + b.helped - (a.wrong + a.helped))
    .slice(0, 5);
  const answers = store.events
    .filter((e) => e.type === "answer")
    .map((e) => e.data as unknown as Answer);
  const first = answers.filter(
    (a) =>
      a.attempt === 1 &&
      !a.task.practiceOnly &&
      ["recall", "application", "missing"].includes(a.task.kind),
  );
  const correct = first.filter((a) => a.correct && !a.hinted);
  const assessment = store.events.filter((e) => e.type === "assessment");
  const download = () => {
    const url = URL.createObjectURL(
      new Blob([store.exportData()], { type: "application/json" }),
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = `小岛学习记录-${day(Date.now())}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const auth = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await store.auth(action, username, password, importGuest);
      setPassword("");
      setNotice("家庭账号已连接，同一网址登录后即可继续这座小岛。");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const daysElapsed = store.events.length
    ? Math.floor((Date.now() - Date.parse(store.events[0].at)) / 86400000)
    : 0;
  return (
    <Modal title="家长小站" onClose={onClose} wide>
      <p className="modal-intro">看见学习的过程，也看见每一次愿意尝试。</p>
      <div className="parent-tabs">
        {[
          { id: "learning", text: "学习观察" },
          { id: "account", text: "家庭同步" },
          { id: "settings", text: "伙伴与设置" },
        ].map((t) => (
          <button
            key={t.id}
            className={tab === t.id ? "active" : ""}
            onClick={() => {
              setTab(t.id as typeof tab);
              setNotice("");
            }}
          >
            {t.text}
          </button>
        ))}
      </div>
      {tab === "learning" && (
        <div className="parent-learning">
          <div className="stat-grid">
            <div>
              <b>
                {first.length
                  ? `${Math.round((correct.length / first.length) * 100)}%`
                  : "—"}
              </b>
                <span>乘法首次无提示正确率</span>
              <small>{first.length}次独立计算题的首次尝试</small>
            </div>
            <div>
              <b>
                {learned.length}
                <small> / 45</small>
              </b>
              <span>初步掌握的口诀</span>
              <small>跨日答对，且有理解证据</small>
            </div>
            <div>
              <b>{[...goals.values()].filter((e) => e.applicationDays.length > 0).length}</b>
              <span>有跨情境应用证据的本领</span>
              <small>不把选择题猜中算作迁移</small>
            </div>
          </div>
          <div className="parent-section">
            <h3>乘法本领地图</h3>
            <p className="note">
              横纵轴是两个乘数。同一句口诀的两个方向共用理解记录；每个方向仍分别练习。颜色不是成绩等级。
            </p>
            <div className="heatmap">
              {Array.from({ length: 10 }, (_, row) =>
                Array.from({ length: 10 }, (_, col) =>
                  row === 0 || col === 0 ? (
                    <span className="heat-label" key={`${row}-${col}`}>
                      {row === 0 ? col || "×" : row}
                    </span>
                  ) : (
                    <span
                      key={`${row}-${col}`}
                      className={`heat-cell ${p.get(`${Math.min(row, col)}x${Math.max(row, col)}`)!.status}`}
                      title={`${row}×${col}：${rhyme(row, col)}`}
                    >
                      {row * col}
                    </span>
                  ),
                ),
              )}
            </div>
            <div className="legend">
              <span>
                <i className="new" />
                还未观察
              </span>
              <span>
                <i className="learning" />
                正在学习
              </span>
              <span>
                <i className="initial" />
                初步掌握
              </span>
              <span>
                <i className="steady" />
                较稳固
              </span>
            </div>
          </div>
          <div className="parent-section">
            <h3>本学期17项数学本领</h3>
            <p className="note">只显示实际作答留下的证据。空白表示尚未观察，不表示不会。</p>
            <p className="note">旧版答题记录会保留，但不会再直接判成已掌握。这里的“解释”和“现实测量”只由家长手动记录，不录音、不上传孩子语音。</p>
            <div className="concept-grid">
              {[...goals.entries()].map(([id, evidence]) => {
                const initial = ["independent", "flexible", "steady"].includes(evidence.status);
                const status = ({ new: "还未观察", legacy: "历史记录，待新版验证", exploring: "正在探索", independent: "有跨日独立证据", flexible: "能迁移使用", steady: "较稳固", "needs-practice": "最近需要复习" } as const)[evidence.status];
                return (
                  <article className={`concept-card ${initial ? "evidenced" : ""}`} key={id}>
                    <span className="eyebrow">{id}</span>
                    <strong>{conceptNames[id]}</strong>
                    <span>{status}</span>
                    <small>
                      辨认：{evidence.recognitionCount}次 · 操作：{evidence.understandingDays.length}天 · 独立：{evidence.independentDays.length}天
                    </small>
                    <small>迁移：{evidence.applicationDays.length}天 · 家长观察解释：{evidence.expressionDays.length}天 · 现实测量：{evidence.physicalDays.length}天</small>
                    <div className="guardian-observations">
                      <button className="text-button" onClick={() => store.add("guardian-observation", { conceptId: id, observationType: "spoken-explanation", contextId: "家庭陪伴观察" })}>我听到孩子解释了</button>
                      {id.startsWith("MEAS-") && <button className="text-button" onClick={() => store.add("guardian-observation", { conceptId: id, observationType: "real-world-measurement", contextId: "家庭真实测量" })}>已在现实中测量</button>}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
          <div className="parent-section">
            <h3>最近更需要陪伴的地方</h3>
            {helped.length ? (
              helped.map((f) => (
                <div className="evidence-row" key={f.key}>
                  <strong>
                    {rhyme(
                      ...(f.key.split("x").map(Number) as [number, number]),
                    )}
                  </strong>
                  <span>
                    {f.wrong}次错误尝试 · {f.helped}次帮助下完成
                  </span>
                  <small>
                    {f.understanding ? "已有操作理解记录" : "还需要摆一摆"}
                  </small>
                </div>
              ))
            ) : (
              <p className="note">
                还没有足够记录。先完成一次小委托，不根据空白数据判断孩子的水平。
              </p>
            )}
            <div className="home-practice">
              <Heart size={22} />
              <div>
                <strong>今天的一分钟生活小游戏</strong>
                <p>
                  {helped.length
                    ? (() => {
                        const [a, b] = helped[0].key.split("x").map(Number);
                        return `拿${a}个小碟子，每碟放${b}颗豆子。请孩子先说一共多少，再摆出来验证；最后问：“你是怎么想的？”`;
                      })()
                    : "摆2个盘子，每盘3颗豆子。请孩子先猜总数，再摆出来验证。听他解释，不急着告诉答案。"}
                </p>
              </div>
            </div>
          </div>
          <div className="parent-section">
            <h3>两周学习观察</h3>
            <p className="note">
              使用不同形式检查相同能力。结果是家庭观察，不能单独证明教学效果。提示后的答案不计入独立正确。
            </p>
            <div className="assessment-cards">
              {[0, 7, 14].map((n) => {
                const e = assessment
                  .filter((e) => e.data.checkpoint === n)
                  .at(-1);
                const as = answers.filter(
                  (a) => a.session === e?.data.session && a.attempt === 1,
                );
                return (
                  <div key={n}>
                    <strong>{n === 0 ? "起点观察" : `第${n}天观察`}</strong>
                    <p>
                      {e
                        ? `${as.filter((a) => a.correct && !a.hinted).length} / ${as.length} 首次独立答对`
                        : n > daysElapsed
                          ? `冒险满${n}天后开放`
                          : "尚未记录"}
                    </p>
                    <button
                      className="secondary"
                      disabled={n > daysElapsed}
                      onClick={() => onAssessment(n)}
                    >
                      {e ? "再观察一次" : "开始观察"}
                    </button>
                  </div>
                );
              })}
            </div>
            <button className="text-button" onClick={() => window.print()}>
              <Printer size={16} />
              打印学习记录和纸面小任务
            </button>
            <div className="print-only">
              <h2>离开屏幕，也会用吗？</h2>
              <p>1. 3 × 4 = ____　2. 5 × 6 = ____　3. 7 × 2 = ____</p>
              <p>4. 有4个篮子，每篮放3个苹果。一共有多少个？</p>
              <p>
                请孩子用画图或摆物品解释一种方法。家长记录：是否独立完成？是否愿意继续？
              </p>
            </div>
          </div>
        </div>
      )}
      {tab === "account" && (
        <div className="account-panel">
          <div className="sync-banner">
            {store.online ? <Cloud /> : <CloudOff />}
            <div>
              <strong>
                {store.account
                  ? `已连接：${store.account.username}`
                  : "让手机和电脑，住着同一座小岛"}
              </strong>
              <p>
                {store.account
                  ? store.syncing
                    ? "正在同步学习记录…"
                    : store.pending
                      ? `${store.pending}条记录等待同步`
                      : "学习记录已保存至家庭服务"
                  : "家长创建家庭账号，孩子只使用昵称。在其他设备打开同一网址并登录即可同步。"}
              </p>
            </div>
          </div>
          {store.message && (
            <p className="form-error" role="alert">
              {store.message}
            </p>
          )}
          {!store.account ? (
            <form onSubmit={auth}>
              <div className="auth-switch">
                <button
                  type="button"
                  className={action === "register" ? "active" : ""}
                  onClick={() => setAction("register")}
                >
                  创建家庭
                </button>
                <button
                  type="button"
                  className={action === "login" ? "active" : ""}
                  onClick={() => setAction("login")}
                >
                  已有家庭，登录
                </button>
              </div>
              <label>
                家庭名
                <input
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  minLength={3}
                  maxLength={24}
                  required
                  placeholder="3–24位中文、字母或数字"
                />
              </label>
              <label>
                家庭密码
                <input
                  type="password"
                  autoComplete={
                    action === "register" ? "new-password" : "current-password"
                  }
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  maxLength={128}
                  required
                  placeholder="至少8位，请家长妥善保存"
                />
              </label>
              {store.events.length > 0 && (
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={importGuest}
                    onChange={(e) => setImportGuest(e.target.checked)}
                  />
                  把这台设备的试玩记录并入这个家庭
                </label>
              )}
              <button className="primary full" disabled={busy || !store.online}>
                {busy
                  ? "正在连接…"
                  : action === "register"
                    ? "创建家庭并开启同步"
                    : "登录我的家庭"}
              </button>
              <p className="note">
                请记好家庭名和密码。首版不收集邮箱，也未提供自助找回密码。
              </p>
            </form>
          ) : (
            <div className="account-buttons">
              <button
                className="primary"
                onClick={() => void store.sync()}
                disabled={store.syncing || !store.online}
              >
                <RefreshCw size={18} />
                立即同步
              </button>
              <button
                className="secondary"
                disabled={store.pending > 0 || !store.online}
                onClick={async () => {
                  try {
                    await store.logout();
                    setNotice("已退出家庭，回到本机试玩。家庭记录仍然保留。");
                  } catch (e) {
                    setError((e as Error).message);
                  }
                }}
              >
                <LogOut size={18} />
                退出家庭
              </button>
              <p className="note">
                有待同步记录时先保存，再退出，避免将孩子的记录混入其他家庭。
              </p>
            </div>
          )}
          <button className="text-button" onClick={download}>
            <Download size={17} />
            导出学习记录备份
          </button>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
      {tab === "settings" && (
        <div className="settings-panel">
          <div className="settings-pet">
            <Animal size={115} />
            <p>你的专属小伙伴，会记得你的名字。</p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              store.add("profile", { child: child.trim(), pet: pet.trim() });
              setNotice("名字已经记好啦。");
            }}
          >
            <label>
              孩子的昵称
              <input
                maxLength={16}
                required
                value={child}
                onChange={(e) => setChild(e.target.value)}
              />
            </label>
            <label>
              小海獭的名字
              <input
                maxLength={16}
                required
                value={pet}
                onChange={(e) => setPet(e.target.value)}
              />
            </label>
            <button className="primary" disabled={!child.trim() || !pet.trim()}>
              保存名字 <Check size={18} />
            </button>
          </form>
          <div className="parent-section">
            <h3>我们的学习约定</h3>
            <p>
              每次约10分钟，任务内不倒计时；允许随时退出续玩。遇到困难先给帮助，不扣生命、不清零连续记录。
            </p>
            <p>
              课程按能力组织，当前交付为数感补练和乘法。除法、测量等后续内容未开放；确认学校教材后再对齐单元。
            </p>
            <p>
              同一口诀至少跨两个日期独立答对，且有数量理解证据，才标为初步掌握。较稳固还需要应用与延迟复习证据。
            </p>
          </div>
        </div>
      )}
      {notice && (
        <p className="form-success" role="status">
          {notice}
        </p>
      )}
    </Modal>
  );
}
